import React from "react";
import { Card, Typography } from "@kelp-dao/kernel-design-system";
import { useRouter } from "next/router";
import BaseImage from "@components/BaseImage";
import { VaultItem } from "./types";
import { trackEvent } from "utils";
import { CLICK_VAULT_CARD } from "@constants/analytics";
import Insured from "../Common/insured";

interface VaultCardProps {
  vault: VaultItem;
  onClick?: (vault: VaultItem) => void;
}

const VaultCard: React.FC<VaultCardProps> = ({ vault, onClick }) => {
  const router = useRouter();

  const handleClick = () => {
    if (onClick) {
      onClick(vault);
    } else {
      if (vault.isExternal) {
        window.open(vault.link, "_blank");
        trackEvent(CLICK_VAULT_CARD, {
          type: vault.key,
          category: vault.category,
        });
      } else {
        router.push(vault.link);
        trackEvent(CLICK_VAULT_CARD, {
          type: vault.key,
          category: vault.category,
        });
      }
    }
  };
  return (
    <Card
      className="p-6 rounded-xl bg-white border border-none hover:shadow-md transition-all duration-200 cursor-pointer relative"
      variant="non-bordered"
      onClick={handleClick}
    >
      {vault.insuredLink && (
        <div className="absolute top-0 right-[98px] z-10">
          <Insured variant="tag" link={vault.insuredLink} />
        </div>
      )}
      {vault.topRightTag && (
        <div
          className={`absolute top-0 right-0 px-3 py-2 rounded-xl rounded-br-none rounded-tl-none flex items-center justify-center gap-1`}
          style={{
            backgroundColor: vault.topRightTag?.bgColor || "#00B7A833",
          }}
        >
          <BaseImage
            src={vault.topRightTag?.icon || ""}
            alt={vault.topRightTag?.label || ""}
            width={16}
            height={16}
          />
          <Typography
            type="misc"
            variant="label"
            as="p"
            className={`text-[${vault.topRightTag?.textColor || "#000000"}]`}
          >
            {vault.topRightTag?.label}
          </Typography>
        </div>
      )}
      <div className="flex flex-col gap-2">
        {/* Logo */}
        <div className="w-max h-max rounded-full bg-white border border-border-default flex items-center justify-center">
          <BaseImage
            src={vault.icon}
            alt={vault.title}
            width={40}
            height={40}
            className="!w-10 !h-10 !object-contain"
          />
        </div>

        {/* Title and Description Group */}
        <div className="flex flex-col">
          {/* Name (with partner tag if any) */}
          <div className="flex items-center gap-2 min-h-[24px]">
            <Typography
              type="heading"
              variant="xxsmall"
              className="text-text-primary font-medium line-clamp-1 font-sans"
            >
              {vault.title}
            </Typography>
            {vault.renderTag && vault.renderTag()}
          </div>

          {/* Description */}
          <Typography
            type="body"
            variant="xsmall"
            className="text-text-secondary line-clamp-4 sm:min-h-[40px] mt-1"
          >
            {vault.description}
          </Typography>
        </div>

        {/* Metrics (TVL & APY) */}
        <div className="w-full flex justify-start gap-[18px] mt-8">
          {vault.tags.map((tag, index) => (
            <div
              key={tag.label + index}
              className={`flex flex-col gap-0.5 items-start text-left`}
            >
              {tag.isLoading ? (
                <div className="h-5 w-16 bg-gray-200 rounded animate-pulse" />
              ) : tag.renderValue ? (
                tag.renderValue()
              ) : (
                <Typography
                  type="heading"
                  variant="xxsmall"
                  className="text-text-primary font-normal text-sm font-sans"
                >
                  {tag.value}
                </Typography>
              )}
              <Typography
                type="body"
                variant="xsmall"
                className="text-text-secondary font-regular text-[10px]"
              >
                {tag.label}
              </Typography>
            </div>
          ))}
        </div>

        {/* Rewards */}
        {/* {(vault.renderRewards || vault.rewards) && (
          <div className="flex items-center gap-2 min-h-[24px]">
            {vault.renderRewards && (
              <div className="flex items-center">{vault.renderRewards()}</div>
            )}
            {!!vault.rewards && (
              <Typography
                type="body"
                variant="xsmall"
                className="text-text-secondary font-medium truncate"
              >
                {vault.rewards}
              </Typography>
            )}
          </div>
        )} */}

        {/* Note/Warning */}
        {vault.note && <div className="pt-1">{vault.note()}</div>}
      </div>
    </Card>
  );
};

export default VaultCard;
